// ─── Tracking Layer v2 — Geolocation Module ───
// Coleta lat/lng via navigator.geolocation, persiste em localStorage com TTL
// variavel por tipo de entrada (ver constantes TTL_* abaixo).
// Dispara o evento 'geo_ready' via window.__tracking.trackEvent (passa pelos
// adapters registrados — GTMAdapter, Rudder, etc). O GTM eh responsavel por
// ler geo_lat/geo_lng/geo_accuracy e propagar como variable para outras tags.
// So pede permissao se nao houver cache valido.
// Envolvido em IIFE para isolar simbolos — ver comentario em tracking-core.ts.

(() => {
  const STORAGE_KEY = "__insider_tracking_geo";

  // TTL por tipo de entrada no cache. A ideia eh nao punir o usuario por um
  // timeout acidental (nao viu o prompt, internet lenta), mas respeitar uma
  // negacao explicita. Unavailable fica no meio — pode ser falha temporaria.
  const TTL_SUCCESS_MS = 24 * 60 * 60 * 1000; // 24h — lat/lng nao muda com frequencia
  const TTL_DENIED_MS = 24 * 60 * 60 * 1000; // 24h — respeitar decisao do usuario
  const TTL_UNAVAILABLE_MS = 60 * 60 * 1000; // 1h — dispositivo pode recuperar sinal
  const TTL_TIMEOUT_MS = 5 * 60 * 1000; // 5min — tenta de novo logo, usuario pode ter perdido o prompt

  // Codigos do GeolocationPositionError (W3C):
  //   1 = PERMISSION_DENIED    — usuario negou (ou policy bloqueou)
  //   2 = POSITION_UNAVAILABLE — dispositivo sem GPS/sinal pra triangular
  //   3 = TIMEOUT              — passou `timeout` sem resposta
  const ERR_DENIED = 1;
  const ERR_UNAVAILABLE = 2;
  const ERR_TIMEOUT = 3;

  function tracking(): TrackingServiceAPI | undefined {
    return (window as any).__tracking as TrackingServiceAPI | undefined;
  }

  function log(...args: unknown[]): void {
    tracking()?.log("geolocation", ...args);
  }

  function isSuccess(entry: TrackingGeoCache): entry is TrackingGeoSuccess {
    return typeof (entry as TrackingGeoSuccess).lat === "number";
  }

  function ttlFor(entry: TrackingGeoCache): number {
    if (isSuccess(entry)) return TTL_SUCCESS_MS;
    if (entry.error === ERR_DENIED) return TTL_DENIED_MS;
    if (entry.error === ERR_TIMEOUT) return TTL_TIMEOUT_MS;
    return TTL_UNAVAILABLE_MS;
  }

  function readCache(): TrackingGeoCache | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as TrackingGeoCache;
      if (!parsed || typeof parsed.ts !== "number") return null;
      if (Date.now() - parsed.ts > ttlFor(parsed)) return null;
      return parsed;
    } catch {
      return null;
    }
  }

  function writeCache(entry: TrackingGeoCache): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
    } catch {
      // storage bloqueado (modo anonimo estrito) — nada a fazer
    }
  }

  function emitSuccess(entry: TrackingGeoSuccess, source: "fresh" | "cached"): void {
    tracking()?.trackEvent("geo_ready", {
      geo_lat: entry.lat,
      geo_lng: entry.lng,
      geo_accuracy: entry.accuracy,
      geo_status: source === "cached" ? "cached" : "ok",
    });
    log("resolved", source, entry);
  }

  function errorCodeToStatus(code: number): string {
    if (code === ERR_DENIED) return "denied";
    if (code === ERR_UNAVAILABLE) return "unavailable";
    if (code === ERR_TIMEOUT) return "timeout";
    return "unknown";
  }

  function emitFailure(code: number, source: "fresh" | "cached"): void {
    const status = errorCodeToStatus(code);
    tracking()?.trackEvent("geo_ready", {
      geo_status: source === "cached" ? status + "_cached" : status,
    });
    log("failure", source, status, code);
  }

  function requestFresh(): void {
    if (!("geolocation" in navigator)) {
      writeCache({ error: ERR_UNAVAILABLE, ts: Date.now() });
      tracking()?.trackEvent("geo_ready", { geo_status: "unsupported" });
      log("unsupported");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const entry: TrackingGeoSuccess = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
          ts: Date.now(),
        };
        writeCache(entry);
        emitSuccess(entry, "fresh");
      },
      (err) => {
        writeCache({ error: err.code, ts: Date.now() });
        emitFailure(err.code, "fresh");
      },
      {
        timeout: 8000,
        maximumAge: 0,
        enableHighAccuracy: false,
      },
    );
  }

  function boot(): void {
    const cached = readCache();
    if (cached) {
      if (isSuccess(cached)) {
        emitSuccess(cached, "cached");
      } else {
        emitFailure(cached.error, "cached");
      }
      return;
    }
    log("no cache, requesting fresh position");
    requestFresh();
  }

  // Pluga no core via onReady — executa imediato se o core ja esta pronto,
  // caso contrario fica na fila ate o bootstrap terminar (adapters ja
  // registrados). Fallback para o evento tracking:ready cobre o caso raro
  // em que este script carrega antes do tracking-core (stub inline ainda
  // em uso — nao tem onReady).
  const t = tracking();
  if (t && typeof t.onReady === "function") {
    t.onReady(boot);
  } else {
    window.addEventListener("tracking:ready", () => boot(), { once: true });
  }
})();
